import {
  Box,
  Header,
  SpaceBetween,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  Table,
  Button,
  ExpandableSection,
  DateRangePickerProps,
  Link,
} from '@cloudscape-design/components';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { PropertyDateFilter } from '../../common/filtering/date-filtering';
import { PropertyFilterOption, PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { DripEmailWithContact } from '../../common/types';
import { DripScopeParams, getDripEmailMeta, getDripEmails } from '../../common/api/dripEmails';
import { ResourceError } from '../Loading';
import { TextQueryFilter } from '../TextQueryFilter';
import { formatDateString, formatDateStringWithTime } from '../../common/formatting/dateFormat';
import { Email } from '../Email';
import { formatName } from '../../common/names';
import { StarsIcon } from '../StarsIcon';
import { DripEmailStatus } from '../DripEmailStatus';
import { DripEmailPreviewModal } from './DripEmailPreviewModal';

export type DripEmailTableProps = {
  now: Date;
  setNow: (now: Date) => void;
  query: PropertyFilterQuery;
  setQuery?: (query: PropertyFilterQuery) => void;
  range?: DateRangePickerProps.Value | null;
  setRange?: (range: DateRangePickerProps.Value | null) => void;
  sortingField?: string;
  setSortingField?: (field: string | undefined) => void;
  sortDirection?: 'asc' | 'desc';
  setSortDirection?: (direction: 'asc' | 'desc') => void;
  hideFilter?: boolean;
  hidePagination?: boolean;
  hideColumns?: string[];
  variant?: 'embedded' | 'borderless' | 'stacked' | 'full-page';
  selectedItems?: DripEmailWithContact[];
  setSelectedItems?: (items: DripEmailWithContact[]) => void;
  textQuery?: string;
  setTextQuery?: (text: string) => void;
  additionalActions?: React.ReactNode;
  scope?: DripScopeParams;
};

export function DripEmailTable(props: DripEmailTableProps) {
  const {
    now,
    setNow,
    query,
    setQuery,
    range,
    setRange,
    sortingField,
    setSortingField,
    sortDirection,
    setSortDirection,
    selectedItems,
    setSelectedItems,
    textQuery,
    setTextQuery,
    scope,
  } = props;
  const navigate = useNavigate();
  const resourceName = 'Drip Emails';
  const endpointName = 'drip_emails';
  const past = true;
  const [previewUuid, setPreviewUuid] = useState<string | null>(null);

  const DEFAULT_PREFERENCES = {
    pageSize: 50,
  };
  const PAGE_SIZE_OPTIONS = [
    { value: 10, label: `10 ${resourceName}` },
    { value: 20, label: `20 ${resourceName}` },
    { value: 50, label: `50 ${resourceName}` },
    { value: 100, label: `100 ${resourceName}` },
  ];

  const [preferences, setPreferences] = useLocalStorage<
    CollectionPreferencesProps.Preferences<unknown>
  >('React-Cards-Preferences', DEFAULT_PREFERENCES);

  const pageSize = preferences?.pageSize || DEFAULT_PREFERENCES.pageSize;

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const metaQuery = useQuery({
    queryKey: [endpointName, 'meta'],
    queryFn: getDripEmailMeta,
    staleTime: 360_000,
  });

  const { options, properties } = useMemo(() => {
    if (metaQuery.data) {
      return {
        options: metaQuery.data.filteringOptions as PropertyFilterOption[],
        properties: metaQuery.data.filteringProperties,
      };
    }
    return { options: [], properties: [] };
  }, [metaQuery.data]);

  const { isPending, isFetching, error, data } = useQuery({
    queryKey: [
      endpointName,
      query,
      range,
      pageSize,
      sortingField,
      sortDirection,
      page,
      now,
      textQuery,
      scope?.campaign_uuid ?? null,
      scope?.zip_blast_uuid ?? null,
    ],
    enabled: metaQuery.isSuccess,
    queryFn: async () =>
      await getDripEmails(
        query,
        page,
        pageSize,
        range,
        sortingField,
        sortDirection,
        now,
        textQuery,
        scope,
      ),
    staleTime: 60_000,
  });

  const loading =
    (isPending || isFetching || metaQuery.isPending || metaQuery.isFetching) &&
    !error &&
    !metaQuery.error;

  useEffect(() => {
    if (data && data.pages !== pageCount) {
      setPageCount(data.pages);
    }
  }, [data]);

  const refresh = () => {
    setNow(new Date());
    metaQuery.refetch();
  };

  const pagination = (
    <Pagination
      currentPageIndex={page}
      disabled={loading}
      pagesCount={pageCount}
      onChange={({ detail }) => setPage(detail.currentPageIndex)}
    />
  );

  return (
    <>
    <Table
      onSortingChange={({ detail }) => {
        setSortingField?.(detail.sortingColumn.sortingField);
        setSortDirection?.(detail.isDescending ? 'desc' : 'asc');
      }}
      sortingColumn={{
        sortingField,
      }}
      sortingDescending={sortDirection === 'desc'}
      header={
        <Header
          variant={
            !props.variant
              ? 'awsui-h1-sticky'
              : props.variant !== 'full-page'
                ? 'h2'
                : 'awsui-h1-sticky'
          }
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              {props.additionalActions}
              <Button variant="normal" iconName="refresh" onClick={refresh} disabled={loading} />
            </SpaceBetween>
          }
          counter={
            data?.total && metaQuery.data?.total
              ? `(${data.total.toLocaleString()}/${metaQuery.data.total.toLocaleString()})`
              : undefined
          }
        >
          {resourceName}
        </Header>
      }
      items={data?.items ?? []}
      selectedItems={selectedItems}
      onSelectionChange={
        setSelectedItems ? ({ detail }) => setSelectedItems(detail.selectedItems) : undefined
      }
      selectionType={setSelectedItems ? 'multi' : undefined}
      loading={loading}
      loadingText={`Loading ${resourceName}...`}
      pagination={props.hidePagination ? undefined : pagination}
      preferences={
        <CollectionPreferences
          disabled={loading}
          pageSizePreference={{ options: PAGE_SIZE_OPTIONS }}
          preferences={preferences}
          onConfirm={(e) => setPreferences(e.detail)}
        />
      }
      filter={
        props.hideFilter ? undefined : (
          <SpaceBetween direction="vertical" size="xxxs">
            <TextQueryFilter
              loading={loading}
              text={textQuery ?? ''}
              setText={(q) => {
                setTextQuery?.(q);
                setPage(1);
              }}
            />
            <ExpandableSection headerText="Additional filters">
              <PropertyDateFilter
                loading={loading}
                query={query}
                setQuery={(q) => {
                  setQuery?.(q);
                  setPage(1);
                }}
                range={range ?? null}
                setRange={(r) => {
                  setRange?.(r);
                  setPage(1);
                }}
                propertyFilteringOptions={options}
                filteringProperties={properties}
                past={past}
              />
            </ExpandableSection>
          </SpaceBetween>
        )
      }
      variant={props.variant ?? 'full-page'}
      stickyHeader={!props.variant || props.variant === 'full-page'}
      empty={
        <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
          {error || metaQuery.error ? (
            <ResourceError
              error={error ?? metaQuery.error ?? new Error('Unknown error')}
              refetch={refresh}
              resourceName={resourceName}
            />
          ) : (
            <SpaceBetween size="xxs">
              <div>
                <b>No {resourceName.toLowerCase()}</b>
                <Box color="inherit" variant="p">
                  No {resourceName.toLowerCase()} found.
                </Box>
              </div>
            </SpaceBetween>
          )}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'contact',
          header: 'Contact',
          cell: (item: DripEmailWithContact) => (
            <SpaceBetween direction="vertical" size="xxxs">
              <Link
                href={`/dashboard/drip/contacts/${item.contact.uuid}`}
                variant="secondary"
                target="_blank"
                // onFollow={(e) => {
                //   e.preventDefault();
                //   navigate(`/contacts/${item.uuid}`);
                // }}
              >
                {formatName(item.contact.first_name, item.contact.last_name)}
              </Link>
              <Box variant="span">
                {item.contact.job_title ?? '-'}
                {item.contact.department && (
                  <Box variant="span" color="text-body-secondary">
                    {` - ${item.contact.department}`}
                  </Box>
                )}
              </Box>
              <Email email={item.contact.email} />
            </SpaceBetween>
          ),
          sortingField: 'first_name',
        },
        {
          id: 'place',
          header: 'Place',
          minWidth: 220,
          cell: (item: DripEmailWithContact) =>
            item.contact.place ? (
              <Header
                variant="h2"
                description={
                  <SpaceBetween direction="vertical" size="xxxs">
                    {item.contact.place.address}
                    <SpaceBetween direction="horizontal" size="xs">
                      {item.contact.place.rating?.toFixed(1) ?? '?.?'}
                      <StarsIcon stars={item.contact.place.rating ?? undefined} />
                      <Link href={item.contact.place.cid_url ?? undefined} target="_blank" variant="secondary">
                        {item.contact.place.total_review_count?.toLocaleString() ?? '?'} Google
                        reviews
                      </Link>
                    </SpaceBetween>
                  </SpaceBetween>
                }
              >
                <Link
                  href={`/dashboard/places/${item.contact.place.uuid}`}
                  variant="secondary"
                  target="_blank"
                  // onFollow={(e) => {
                  //   e.preventDefault();
                  //   navigate(`/places/${item.uuid}`);
                  // }}
                >
                  {item.contact.place.name}
                </Link>
              </Header>
            ) : (
              <Box variant="span">-</Box>
            ),
        },
        {
          id: 'status',
          header: 'Status',
          cell: (item: DripEmailWithContact) => <DripEmailStatus item={item} />,
        },
        {
          id: 'preview',
          header: 'Preview',
          cell: (item: DripEmailWithContact) => (
            <Button
              variant="inline-link"
              iconName="view-full"
              ariaLabel={`Preview email to ${item.contact.email}`}
              onClick={() => setPreviewUuid(item.uuid)}
            >
              Preview
            </Button>
          ),
          width: 130,
        },
      ].filter((column) => !props.hideColumns?.includes(column.id))}
    />
    <DripEmailPreviewModal emailUuid={previewUuid} onDismiss={() => setPreviewUuid(null)} />
    </>
  );
}
